/**
 * Map an authenticated /api/user/sessions DTO row (see
 * services/userSessionsService.js#toUserSessionDto on the backend) into
 * the flat shape ChargingSessionCard / ReceiptSummary already consume.
 *
 * Deliberately narrow: only the fields those components reference, no
 * receipt email/name, no processor payloads, no admin-only columns.
 * Amounts are converted dollars → cents for consistency with the local
 * history store's `capturedCents` contract.
 */

import { isCanonicalSnapshot } from '../chargerUx/canonical';

function toNumber(x) {
  if (x == null || x === '') return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function dollarsToCents(x) {
  const n = toNumber(x);
  if (n == null) return null;
  return Math.round(n * 100);
}

/**
 * Normalise the billing unit the backend records. `ev_sessions.billing_unit`
 * is one of `kwh` / `min` today; older rows may be null. We canonicalise
 * to a small enum the receipt view understands.
 */
function normaliseBillingUnit(v) {
  if (typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  if (s === 'kwh') return 'kwh';
  if (s === 'min' || s === 'minute' || s === 'minutes') return 'min';
  if (s === 'session' || s === 'flat') return 'session';
  return null;
}

export function adaptAccountSession(row) {
  if (!row || typeof row !== 'object' || !row.sessionUid) return null;
  const stationMeta = row.stationMeta || {};
  const vehicle = row.vehicle || {};
  const pricing = row.pricing || {};
  const cost = row.cost || {};
  const payment = row.payment || {};
  const energy = row.energy || {};
  const budget = row.budget || {};
  // Backend-added credit field (see /api/user/sessions DTO). Always
  // reference via a defensive `|| {}` so an older backend that hasn't
  // been deployed yet still produces a usable record.
  const credit = row.credit || {};

  return {
    sessionUid: row.sessionUid,
    stationId: row.stationId || null,
    connectorId: row.connectorId ?? null,
    status: row.status || null,
    stationName: stationMeta.name || null,
    stationLocation: stationMeta.location || null,
    plateRaw: vehicle.plate || null,
    plateNormalized: vehicle.plateNormalized || null,
    vehicleName: vehicle.name || null,
    // Canonical billing mode. The DTO carries it in two places
    // (session-level `pricing.billingMode` and vehicle-level
    // `vehicle.billingMode`); the session-level value is
    // authoritative because that's what was recorded on the actual
    // session row. The vehicle value is only a fallback for very old
    // sessions that predate the per-session capture.
    billingMode: pricing.billingMode || vehicle.billingMode || null,
    startedAt: row.startedAt || null,
    endedAt: row.endedAt || row.stoppedAt || null,
    energyKwh: toNumber(energy.kwh),
    currency: pricing.currency || 'BBD',
    // Pre-authorisation vs. captured — kept strictly separate so the
    // receipt can render both without confusion.
    authorizedCents: dollarsToCents(budget.authorizedAmount),
    capturedCents: dollarsToCents(cost.total),
    paymentStatus: payment.status || null,
    ratePerKwh: toNumber(pricing.ratePerKwh),
    // Passed through so the rate row can render `/min` when the
    // session was billed per-minute rather than per-kWh.
    ratePerMinute: toNumber(pricing.ratePerMinute),
    // Canonical unit for the rate row. `null` means we don't know —
    // the receipt should fall back to whichever rate field is
    // populated but never invent a unit label.
    billingUnit: normaliseBillingUnit(pricing.billingUnit),
    sessionFee: toNumber(pricing.sessionFee),
    // Credit / reimbursement fields. The DTO surfaces the sum of any
    // redeemed codes tied to this session and their currency; the
    // adapter converts dollars → cents to match the cost / auth
    // fields.
    creditAppliedCents: dollarsToCents(credit.appliedAmount),
    creditCurrency: typeof credit.currency === 'string' ? credit.currency : null,
    // Server-built billing contract, carried through untouched. Its
    // `display` is what every billing surface renders (see
    // utils/billingDisplay.js). Null on an API that predates it.
    billing: row.billing && typeof row.billing === 'object' ? row.billing : null,
    // Charger UX: the canonical snapshot, when the API offers one. Passed
    // through only if this build can render it; otherwise null and the
    // record renders exactly as before.
    canonical: isCanonicalSnapshot(row.canonical) ? row.canonical : null,
  };
}

export function adaptAccountSessions(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const row of rows) {
    const rec = adaptAccountSession(row);
    if (rec) out.push(rec);
  }
  return out;
}
